import { useState, useEffect, useRef } from 'react'
import { fetchPlayoffBracket } from '../lib/playoffs'
import { saveBracket, loadBracket, gradeBracketPicks } from '../lib/db'
import { timeAgo } from '../lib/time'
import TeamLogoImg from './TeamLogoImg'

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalizeSport(raw) {
  return (raw || 'NBA').toString().toUpperCase().replace(/[🏀🏒⚾🏈]\s*/g, '').trim()
}

// A matchup is real (not a placeholder) when neither team is TBD.
function isRealMatchup(m) {
  return m.team1 !== 'TBD' && m.team2 !== 'TBD'
}

// ── v2 pick shape helpers ────────────────────────────────────────────────────
//
// v2 picks are stored as a flat array of {round, slot, team} entries, keyed
// internally as `${round}:${slot}` for fast lookup. Round is the ESPN-style
// round abbreviation (RD16, QTR, SEMI, FINAL, WC, DS, CS, WS, DIV, CONF, SB).
// Slot is the 0-indexed position within that round's matchups array, set
// during canonicalization in playoffs.js. This is stable across ESPN data
// evolution; matchup IDs are not. See .claude/bracket-slot-refactor-design.md.

// Build a `${round}:${slot}` key from a matchup. The matchup's `roundAbbr`
// and `slot` fields are set in playoffs.js buildBracketFromGames Step 8.
function pickKey(matchup) {
  if (!matchup) return null
  const round = matchup.roundAbbr
  const slot  = matchup.slot
  if (!round || typeof slot !== 'number') return null
  return `${round}:${slot}`
}

// Look up the picked team for a given matchup. Returns the team name string
// or undefined. `picks` is a Map keyed by `${round}:${slot}`.
function getPickForMatchup(picks, matchup) {
  const key = pickKey(matchup)
  if (!key || !picks) return undefined
  if (picks instanceof Map) return picks.get(key)
  return picks[key]
}

// ── v1 → v2 migration ────────────────────────────────────────────────────────
//
// Old picks were stored as a flat `{matchupId: team}` map where matchupId
// followed one of two grammars: ESPN-real (`TeamA|TeamB|RoundAbbr`) or
// stable hash (`Round:TeamA|TeamB`). Both depended on which teams happened
// to occupy the matchup, breaking when ESPN published real series and the
// IDs shifted grammars.
//
// Migration walks every v1 entry and tries to map it to a {round, slot}
// address in the current live bracket. Picks that can't be reconciled are
// preserved under migration_warnings so we never silently lose data.
//
// The live bracket here is the post-canonicalization output of
// fetchPlayoffBracket — every matchup has stable `roundAbbr` and `slot`.
function migratePicksV1toV2(savedPicks, liveBracket) {
  // Already v2 — return as-is. `picks` is the array, lengths preserved.
  if (savedPicks && savedPicks.v === 2 && Array.isArray(savedPicks.picks)) {
    return savedPicks
  }

  // Defensive: no saved picks at all. Return empty v2.
  if (!savedPicks || typeof savedPicks !== 'object') {
    return { v: 2, picks: [], lengths: {}, migration_warnings: [] }
  }

  // Some legacy rows may have nested under `picks`, some may be flat.
  // Accept both shapes.
  const rawMap = (savedPicks.picks && typeof savedPicks.picks === 'object' && !Array.isArray(savedPicks.picks))
    ? savedPicks.picks
    : savedPicks
  const lengths = savedPicks.lengths || rawMap.__lengths || {}

  const v2Picks  = []
  const warnings = []

  // Flatten live bracket into a lookup. Two indices:
  //   byId:           ESPN-real id → matchup (for `TeamA|TeamB|Round` keys)
  //   byRoundAndTeam: `${roundAbbr}:${team}` → matchup (for stable-hash keys)
  const byId = new Map()
  const byRoundAndTeam = new Map()
  ;(liveBracket?.rounds || []).forEach(round => {
    round.matchups.forEach(m => {
      if (m.id) byId.set(m.id, m)
      const ra = m.roundAbbr
      if (ra) {
        if (m.team1 && m.team1 !== 'TBD') byRoundAndTeam.set(`${ra}:${m.team1}`, m)
        if (m.team2 && m.team2 !== 'TBD') byRoundAndTeam.set(`${ra}:${m.team2}`, m)
      }
    })
  })

  for (const [key, value] of Object.entries(rawMap)) {
    if (key === '__lengths') continue
    if (typeof value !== 'string') {
      warnings.push({ original_key: key, team: value, reason: 'non-string pick value' })
      continue
    }
    if (key.includes('placeholder')) {
      warnings.push({ original_key: key, team: value, reason: 'placeholder key, unresolvable' })
      continue
    }
    if (/(^|[\s:|])TBD([\s|:]|$)/.test(key)) {
      warnings.push({ original_key: key, team: value, reason: 'TBD token in key' })
      continue
    }

    // ESPN-real grammar: `TeamA|TeamB|RoundAbbr` — direct ID lookup.
    if (byId.has(key)) {
      const m = byId.get(key)
      v2Picks.push({ round: m.roundAbbr, slot: m.slot, team: value })
      continue
    }

    // Stable-hash grammar: `Round:TeamA|TeamB` — parse and resolve by team.
    // Try matching the picked team against the named round; that's the team
    // the user said advances from that matchup, so it must be in either
    // team1 or team2 of the live matchup in that round.
    const hashMatch = /^([A-Z0-9]+):(.+)$/.exec(key)
    if (hashMatch) {
      const round = hashMatch[1]
      const lookup = byRoundAndTeam.get(`${round}:${value}`)
      if (lookup) {
        v2Picks.push({ round: lookup.roundAbbr, slot: lookup.slot, team: value })
        continue
      }
      // Fallback for single-matchup rounds (FINAL, SB, WS): if the team
      // isn't currently in the matchup (because upstream rounds haven't
      // resolved yet), but the round has exactly one slot, the slot is
      // unambiguous — there's only one place this pick could refer to.
      // The user picked "team X wins the championship"; assign to slot 0.
      const roundObj = (liveBracket?.rounds || []).find(r =>
        r.matchups.some(m => m.roundAbbr === round)
      )
      if (roundObj && roundObj.matchups.length === 1) {
        v2Picks.push({ round, slot: 0, team: value })
        continue
      }
    }

    warnings.push({ original_key: key, team: value, reason: 'no matching matchup in current bracket' })
  }

  return { v: 2, picks: v2Picks, lengths, migration_warnings: warnings }
}

// Convert a v2 picks array into a Map for O(1) lookup at render time.
// Map iteration order is insertion order, matching the array.
function picksArrayToMap(v2) {
  const m = new Map()
  if (!v2 || !Array.isArray(v2.picks)) return m
  v2.picks.forEach(p => {
    if (!p || !p.round || typeof p.slot !== 'number' || !p.team) return
    m.set(`${p.round}:${p.slot}`, p.team)
  })
  return m
}

// Compute which prior-round matchup feeds each slot of each downstream
// matchup. Returns `feedingMap[r]` per round: an array of per-matchup
// feeding info `{ slot1: { r, i } | null, slot2: { r, i } | null }`,
// or null for the first round (no feeder).
//
// Single source of truth for routing — consumed by both buildDisplayBracket
// (to fill TBD slots) and getUserPickForFeedingSlot (to show wrong
// feeder-round picks struck-through under the real advancing team).
// Drift between the two is impossible by construction.
//
// R1 → R2 and CF → Finals are positional (prev[idx*2], prev[idx*2+1]).
// R2 → CF uses conference-aware reseeding because ESPN's R1/R2 ordering is
// interleaved (R1[0]=East, R1[1]=West, …) and R2 reseeds within each
// conference; a simple pair-based map would put the wrong teams together.
// Inference: for each R2 matchup, find which CF it feeds by matching its
// teams against real CF teams. Group R2 indices by target CF. Unallocated
// R2 indices go to TBD CFs first (preserves prior TBD-fill behavior), then
// fully-filled CFs claim any leftovers so the phantom-pick consumer has
// routing for those too.
function computeFeedingMap(displayRounds) {
  const map = displayRounds.map(() => null)

  // R1 → R2
  if (displayRounds[1]) {
    map[1] = displayRounds[1].matchups.map((_, idx) => ({
      slot1: { r: 0, i: idx * 2 },
      slot2: { r: 0, i: idx * 2 + 1 },
    }))
  }

  // R2 → CF — conference-aware
  if (displayRounds[2] && displayRounds[1]) {
    const r2 = displayRounds[1].matchups
    const cf = displayRounds[2].matchups

    const r2ToCfIdx = r2.map(m => {
      const teams = [m.team1, m.team2].filter(t => t !== 'TBD')
      if (!teams.length) return null
      for (let cfIdx = 0; cfIdx < cf.length; cfIdx++) {
        const cfM = cf[cfIdx]
        if (cfM.team1 === 'TBD' && cfM.team2 === 'TBD') continue
        if (teams.some(t => t === cfM.team1 || t === cfM.team2)) return cfIdx
      }
      return null
    })

    const cfSources = cf.map(() => [])
    r2ToCfIdx.forEach((cfIdx, r2Idx) => {
      if (cfIdx !== null) cfSources[cfIdx].push(r2Idx)
    })

    const allocated = new Set()
    cfSources.forEach(arr => arr.forEach(i => allocated.add(i)))
    const unallocated = r2.map((_, i) => i).filter(i => !allocated.has(i))

    // Pass 1 — TBD CFs claim leftovers first (mirrors prior TBD-fill order).
    cf.forEach((m, cfIdx) => {
      if (cfSources[cfIdx].length > 0) return
      if (m.team1 !== 'TBD' && m.team2 !== 'TBD') return
      cfSources[cfIdx] = unallocated.splice(0, 2)
    })
    // Pass 2 — fully-filled CFs claim any remaining leftovers, so the
    // phantom-pick consumer has routing for them. TBD-fill is per-slot
    // guarded downstream so this can't cause overwrites.
    cf.forEach((m, cfIdx) => {
      if (cfSources[cfIdx].length > 0) return
      cfSources[cfIdx] = unallocated.splice(0, 2)
    })

    map[2] = cf.map((_, cfIdx) => ({
      slot1: cfSources[cfIdx][0] != null ? { r: 1, i: cfSources[cfIdx][0] } : null,
      slot2: cfSources[cfIdx][1] != null ? { r: 1, i: cfSources[cfIdx][1] } : null,
    }))
  }

  // CF → Finals
  if (displayRounds[3] && displayRounds[2]) {
    map[3] = displayRounds[3].matchups.map((_, idx) => ({
      slot1: { r: 2, i: idx * 2 },
      slot2: { r: 2, i: idx * 2 + 1 },
    }))
  }

  return map
}

// Build the display bracket. Starts from ESPN-published data and fills any
// remaining TBD slots with user picks (March-Madness preview style). Real
// ESPN-published slots are never overwritten; the fill only touches slots
// whose team is still 'TBD'.
//
// A matchup "advances" the ESPN winner if set, else the user's pick — but
// only when the pick still matches a real team in the matchup (guards
// against stale picks against an ID whose team pair has since changed).
//
// Routing comes from computeFeedingMap — the same map is returned alongside
// the display rounds so the phantom-pick consumer uses identical routing.
function buildDisplayBracket(bracket, userPicks) {
  if (!bracket?.rounds?.length) return bracket

  // Fully-recursive deep clone via JSON round-trip so propagation mutations
  // never reach the original React state object held in `bracket`.
  const displayRounds = JSON.parse(JSON.stringify(bracket.rounds))
  const feedingMap    = computeFeedingMap(displayRounds)

  const getAdvancing = (matchup) => {
    if (!matchup) return null
    if (matchup.winner) return matchup.winner
    const pick = getPickForMatchup(userPicks, matchup)
    // typeof === 'string' guards against malformed pick values (booleans /
    // objects observed in legacy NBA data — origin outside current code).
    if (typeof pick === 'string' &&
        matchup.team1 !== 'TBD' && matchup.team2 !== 'TBD' &&
        (pick === matchup.team1 || pick === matchup.team2)) {
      return pick
    }
    return null
  }

  // A slot is fillable from its feeder when:
  //   (a) it's still literal 'TBD' — the canonical placeholder, OR
  //   (b) it's an ESPN combined-placeholder ("TeamA/TeamB", contains '/')
  //       AND the feeder is still in progress (no winner). Once the feeder
  //       resolves, ESPN updates the placeholder to the real winner on its
  //       own — we only substitute the user's pick while it's genuinely
  //       undecided. Resolved slots (real team names) are never touched.
  const fillable = (slotValue, feeder) =>
    slotValue === 'TBD' ||
    (typeof slotValue === 'string' && slotValue.includes('/') && !feeder?.winner)

  // Per-slot fill from feeders. Guards preserve real ESPN data.
  for (let r = 1; r < displayRounds.length; r++) {
    const feed = feedingMap[r]
    if (!feed) continue
    displayRounds[r].matchups.forEach((matchup, idx) => {
      const f = feed[idx]
      if (!f) return
      if (f.slot1) {
        const feeder = displayRounds[f.slot1.r].matchups[f.slot1.i]
        if (fillable(matchup.team1, feeder)) {
          const a = getAdvancing(feeder)
          if (a) matchup.team1 = a
        }
      }
      if (f.slot2) {
        const feeder = displayRounds[f.slot2.r].matchups[f.slot2.i]
        if (fillable(matchup.team2, feeder)) {
          const a = getAdvancing(feeder)
          if (a) matchup.team2 = a
        }
      }
    })
  }

  return {
    ...bracket,
    rounds: displayRounds,
    finals: displayRounds[displayRounds.length - 1]?.matchups?.[0] || null,
    feedingMap,
  }
}

// Returns the user's pick from the feeding matchup IFF that pick differs
// from the feeder's real winner — i.e. the user MISSED in the feeder.
// Returns null otherwise: no feeder (first round), no recorded source,
// feeder hasn't resolved (no winner), user didn't pick in the feeder, or
// user got it right.
//
// Drives the "phantom pick" annotation: the real advancing team shows
// normally in the downstream slot, with the user's struck-through wrong
// pick rendered below it.
function getUserPickForFeedingSlot(roundIdx, matchupIdx, slotIndex, displayRounds, feedingMap, userPicks) {
  const feed = feedingMap?.[roundIdx]?.[matchupIdx]
  if (!feed) return null
  const source = slotIndex === 0 ? feed.slot1 : feed.slot2
  if (!source) return null
  const feeder = displayRounds[source.r]?.matchups?.[source.i]
  if (!feeder || !feeder.winner) return null
  const userPick = getPickForMatchup(userPicks, feeder)
  if (!userPick) return null
  if (userPick === feeder.winner) return null
  return userPick
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function BracketMode({
  group, user, members, onPointsUpdate, promptAuth = () => {},
  // Pattern #2 freemium tease (bracket prototype — last mode). When
  // `locked` is true, taps still update local bracket state (the user
  // SEES their bracket fill in — the tease) but the centralized
  // runSave is short-circuited: no server INSERT, no localStorage
  // write. This single gate at runSave's entry intercepts BOTH the
  // per-tap auto-save (handlePick → runSave at L490) AND the legacy
  // v1→v2 migration save (load-effect runSave at L450). handleSubmit
  // is gated separately at its top before the in-flight-wait loop —
  // its saveBracket call doesn't go through runSave so a runSave-only
  // gate would miss it. The in-flow "Lock In Bracket" button at L714
  // (NOT position:fixed — scrolls with the bracket grid) stays visible;
  // its onClick fires the gated handleSubmit which routes to
  // onUnlockRequest. A sticky unlock bar (mirroring the four prior
  // modes' bars exactly) renders at the bottom. 041's RESTRICTIVE
  // policies on bracket_picks remain the source of truth — these
  // intercepts prevent the raw 42501 from surfacing. When `locked`
  // is false (default), behavior is byte-identical to pre-Pattern-#2.
  locked = false,
  onUnlockRequest,
  unlocking = false,
  unlockPriceCents = null,
}) {
  const sport           = normalizeSport(group?.sport)
  const startRoundOrder = group?.start_round || 1   // 1-4, from group creation
  // Pool-level lock, set by the server cron when the first playoff game
  // starts. Migration 026's RLS denies bracket_picks UPDATEs once this is
  // non-null — without this gate the UI would accept picks whose saves
  // silently fail.
  const poolLocked      = !!group?.bracket_locked_at

  const [bracket,     setBracket]     = useState(null)
  const [picks,       setPicks]       = useState({})
  const [submitted,   setSubmitted]   = useState(false)
  const [loading,     setLoading]     = useState(true)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [refreshing,  setRefreshing]  = useState(false)

  // Refs mirror state for read access inside async callbacks that run later
  // than their scheduling moment. Synced via useEffect for any state change,
  // PLUS updated inline at call sites where the ref must be current before
  // the next render commits (e.g. handlePick → runSave in the same tick).
  const picksRef     = useRef(picks)
  const submittedRef = useRef(false)
  useEffect(() => { picksRef.current     = picks     }, [picks])
  useEffect(() => { submittedRef.current = submitted }, [submitted])

  // Coalescing single-in-flight save queue. Concurrent runSave calls
  // collapse into one in-flight write + one trailing write, eliminating
  // races between rapid pick clicks and the lock-in handler.
  const saveInFlight = useRef(false)
  const savePending  = useRef(false)
  async function runSave() {
    // Anonymous-browsing gate — saveBracket needs user.id; pop the Auth
    // overlay and stop if logged out. Single gate at runSave covers both
    // the per-tap auto-save (handlePick → runSave) AND the legacy v1→v2
    // migration save (load-effect runSave), matching the same single-
    // gate pattern the locked-pool intercept below uses.
    if (!user?.id) return promptAuth()
    // Pattern #2 intercept — locked pool: skip the save entirely (BOTH
    // the per-tap auto-save invoked from handlePick AND the legacy
    // v1→v2 migration save invoked from the load effect). Local pick
    // state still updates upstream (setPicks in handlePick, the v2
    // normalization in the load effect), but no server INSERT and no
    // localStorage write happens. Picks are ephemeral — lost on unlock
    // or page reload. 041's RESTRICTIVE policy on bracket_picks would
    // 42501 the INSERT anyway.
    if (locked) return
    if (saveInFlight.current) {
      savePending.current = true
      return
    }
    saveInFlight.current = true
    try {
      // picksRef.current is a Map keyed by `${round}:${slot}`. Serialize to
      // the v2 array shape before handing off to saveBracket — DB layer
      // stores JSONB and doesn't know about Maps.
      const v2Picks = Array.from(picksRef.current.entries()).map(([key, team]) => {
        const [round, slotStr] = key.split(':')
        return { round, slot: parseInt(slotStr, 10), team }
      })
      const { error } = await saveBracket(group.id, user.id, {
        picks:     { v: 2, picks: v2Picks },
        submitted: submittedRef.current,
      })
      if (error) console.warn('[bracket] runSave error', error)
    } finally {
      saveInFlight.current = false
      if (savePending.current) {
        savePending.current = false
        runSave()
      }
    }
  }

  // ── Load ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const [saved, live] = await Promise.all([
        loadBracket(group.id, user.id),
        fetchPlayoffBracket(sport),
      ])
      if (cancelled) return
      setBracket(live)

      // v2 migration: normalize whatever loadBracket returned (legacy v1
      // flat map or already-v2 array) into the v2 shape, then build a Map
      // for O(1) render lookup. Picks that can't be reconciled against the
      // current live bracket land in v2.migration_warnings — preserved, not
      // silently dropped.
      const v2 = migratePicksV1toV2(saved?.picks, live)
      const picksMap = picksArrayToMap(v2)
      if (v2.migration_warnings?.length) {
        console.warn('[bracket] migration warnings:', v2.migration_warnings)
      }
      const isLegacyV1   = !saved?.picks?.v || saved.picks.v !== 2
      const droppedCount = v2.migration_warnings?.length || 0
      setPicks(picksMap)
      const isSub = saved?.submitted || false
      setSubmitted(isSub)
      submittedRef.current = isSub
      setLoading(false)
      setLastUpdated(Date.now())

      // Persist the migrated/cleaned state when (a) we just upgraded a v1
      // row to v2, or (b) any warnings were generated. Gated by !isSub
      // because submitted brackets are locked — we still migrate in-memory
      // for display + grading, but never modify the stored record.
      //
      // runSave writes both picks + submitted. In this branch isSub is
      // false (gated above), submittedRef.current was just set to false,
      // so submitted: false gets written — matching the existing DB value.
      // No-op for the submitted column, intended-overwrite for picks with
      // the v2 shape.
      picksRef.current = picksMap
      if (!isSub && (isLegacyV1 || droppedCount > 0)) {
        runSave()
      }

      if (isSub && live) {
        try {
          const earned = await gradeBracketPicks(group.id, user.id, live)
          if (!cancelled && earned) onPointsUpdate?.(0)
        } catch (_) {}
      }
    })()
    return () => { cancelled = true }
  }, [group.id, user.id, sport, startRoundOrder])

  // ── Auto-refresh every 5 minutes ─────────────────────────────────────────

  useEffect(() => {
    if (loading) return
    const id = setInterval(async () => {
      const live = await fetchPlayoffBracket(sport)
      if (!live) return
      setBracket(live)
      setLastUpdated(Date.now())
      if (submittedRef.current) {
        try { await gradeBracketPicks(group.id, user.id, live); onPointsUpdate?.(0) } catch (_) {}
      }
    }, 5 * 60_000)
    return () => clearInterval(id)
  }, [loading, sport, group.id, user.id])

  // ── Pick handler — no cascade, ESPN has the real next-round teams ─────────

  function handlePick(matchup, team) {
    if (submitted || poolLocked) return
    const key = pickKey(matchup)
    if (!key) return
    // Clone the Map so React sees a new reference and re-renders.
    const next = new Map(picks)
    next.set(key, team)
    picksRef.current = next   // sync ref so runSave reads the latest picks
    setPicks(next)
    runSave()
  }

  // ── Manual refresh ────────────────────────────────────────────────────────

  async function handleRefresh() {
    if (refreshing) return
    setRefreshing(true)
    try {
      const live = await fetchPlayoffBracket(sport)
      if (live) {
        setBracket(live)
        setLastUpdated(Date.now())
        if (submittedRef.current) {
          try { await gradeBracketPicks(group.id, user.id, live); onPointsUpdate?.(0) } catch (_) {}
        }
      }
    } finally {
      setRefreshing(false)
    }
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    // Anonymous-browsing gate — handleSubmit calls saveBracket directly
    // (not through runSave), so it needs its own gate.
    if (!user?.id) return promptAuth()
    // Pattern #2 intercept — locked pool: route to unlock checkout
    // instead of running the save dance. Placed at the top, before the
    // in-flight-wait loop (no point waiting on autosaves that runSave
    // already gated out). 041's RESTRICTIVE policy on bracket_picks
    // would 42501 the INSERT anyway. canLockIn validation lives at
    // the button level (L714 `disabled={!canLockIn}`), so this gate
    // only fires when the user has a complete enough-points bracket
    // they're trying to lock in.
    if (locked) {
      onUnlockRequest?.()
      return
    }
    // Wait for any in-flight autosave to finish so we don't race it. A
    // pending handlePick save could otherwise land AFTER our submitted:true
    // upsert and overwrite picks with a stale subset. Capped at 10s — past
    // that we assume the in-flight save is hung, abandon waiting, and let
    // the user retry. Any background completion afterward is harmless.
    let waited = 0
    while (saveInFlight.current && waited < 10000) {
      await new Promise(r => setTimeout(r, 50))
      waited += 50
    }
    if (saveInFlight.current) {
      saveInFlight.current = false
      alert('Save is taking too long. Please check your connection and try locking in again.')
      return
    }
    saveInFlight.current = true
    try {
      // Same v2 serialization as runSave — see comment there.
      const v2Picks = Array.from(picksRef.current.entries()).map(([key, team]) => {
        const [round, slotStr] = key.split(':')
        return { round, slot: parseInt(slotStr, 10), team }
      })
      const { error } = await saveBracket(group.id, user.id, {
        picks:     { v: 2, picks: v2Picks },
        submitted: true,
      })
      if (error) {
        // Don't flip submitted state on failure — keeps the user's picks
        // intact and the Lock In button available so they can retry.
        alert(`Couldn't save bracket — try again.\n${error.message || ''}`.trim())
        return
      }
      setSubmitted(true)
      submittedRef.current = true
      // NOTE: gradeBracketPicks currently does NOT increment total_picks /
      // correct_picks on group_members, so the GroupCard "X picks made"
      // badge will read 0 for bracket groups until that's added. Follow-up.
      try {
        const pts = await gradeBracketPicks(group.id, user.id, bracket)
        onPointsUpdate?.(pts)
      } catch (_) {}
    } finally {
      saveInFlight.current = false
      // Discard any trailing save — handlePick is gated by submitted state
      // and can't fire while we're locked. Running it would just re-upsert
      // the same data.
      savePending.current = false
    }
  }

  // ── Loading / error states ────────────────────────────────────────────────

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
      <div style={{ width: 24, height: 24, border: '2px solid var(--border)', borderTopColor: 'var(--gold)', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin .8s linear infinite' }} />
      Loading bracket…
    </div>
  )

  if (!bracket?.rounds?.length) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>🚫</div>
      <div style={{ fontSize: 15, marginBottom: 6 }}>No bracket data for {sport}</div>
      <div style={{ fontSize: 12 }}>Playoffs may not have started yet.</div>
    </div>
  )

  // ── Derived values ────────────────────────────────────────────────────────

  // Overlay user picks onto ESPN data so future-round TBD slots show the teams
  // the user has picked to advance — March Madness style.
  const displayBracket = buildDisplayBracket(bracket, picks)
  const rounds         = displayBracket?.rounds || []
  const feedingMap     = displayBracket?.feedingMap || []
  const finalsMatchup  = displayBracket?.finals || rounds[rounds.length - 1]?.matchups?.[0]

  const champion = finalsMatchup ? getPickForMatchup(picks, finalsMatchup) : null

  // Score-bar semantics:
  //   totalM = pickable matchups (rounds ≥ startRound, real teams), regardless of completion
  //   totalP = matchups with a user pick (subset of totalM)
  //   earned = cumulative points from correctly-picked confirmed winners
  // Previously this skipped completed rounds entirely, which:
  //   (1) shrank totalP/totalM toward zero as the bracket resolved, hiding
  //       picks the user had locked in earlier
  //   (2) dropped earned points from completed rounds, so "Correct: 0 pts"
  //       could disagree with group_members.points (the stored score from
  //       gradeBracketPicks). Now they stay in lockstep.
  let earned = 0, totalM = 0, totalP = 0
  rounds.forEach(round => {
    if ((round.matchups?.[0]?.roundOrder || 1) < startRoundOrder) return
    round.matchups.forEach(m => {
      if (!isRealMatchup(m)) return
      totalM++
      const userPick = getPickForMatchup(picks, m)
      if (userPick) totalP++
      if (m.winner && userPick === m.winner) earned += round.points
    })
  })

  // For submitted brackets, the denominator should be picks-submitted, not
  // pickable-matchups-by-round. Late-joiners can have matchups in their
  // pickable rounds that were already decided when they joined; those
  // matchups aren't actually pickable for that user. The picks array IS
  // the truthful denominator for a locked-in bracket. For unsubmitted
  // brackets, the matchup count still makes sense as "progress remaining."
  if (submitted) {
    totalM = totalP
  }

  // possible (Max pts): the truthful ceiling depends on whether the user is
  // locked in.
  //   Submitted: max = sum of round.points for each matchup the user
  //     actually picked. Late-joiners can't earn points on matchups they
  //     couldn't pick, so those don't count toward Max.
  //   Unsubmitted: max = round.matchups.length × round.points across
  //     all pickable rounds. Represents the theoretical ceiling if the
  //     user fills every slot.
  const POINTS_PER_ROUND = [1, 2, 4, 8]
  let possible = 0
  if (submitted) {
    rounds.forEach((round, idx) => {
      const order = round.matchups?.[0]?.roundOrder || (idx + 1)
      if (order < startRoundOrder) return
      const pts = round.points || POINTS_PER_ROUND[idx] || 0
      round.matchups.forEach(m => {
        if (!isRealMatchup(m)) return
        if (getPickForMatchup(picks, m)) possible += pts
      })
    })
  } else {
    possible = rounds.reduce((sum, round, idx) => {
      const order = round.matchups?.[0]?.roundOrder || (idx + 1)
      if (order < startRoundOrder) return sum
      const pts = round.points || POINTS_PER_ROUND[idx] || 0
      return sum + (round.matchups?.length || 0) * pts
    }, 0)
  }
  const isComplete   = totalP === totalM && totalM > 0

  // userPossible: points up for grabs from undecided real matchups the user
  // has actually picked. Was the previous `possible` semantics — renamed to
  // disambiguate from the entry-total `possible` computed above.
  let userPossible = 0
  rounds.forEach(round => {
    if ((round.matchups?.[0]?.roundOrder || 1) < startRoundOrder) return
    round.matchups.forEach(m => {
      if (!isRealMatchup(m)) return
      if (m.winner) return
      if (getPickForMatchup(picks, m)) userPossible += round.points
    })
  })

  // Lock threshold scales with the entry's total max instead of being a
  // hardcoded 20. Half of total: full=16, R2+=12, CF-only=8, Finals-only=4.
  const lockThreshold   = Math.floor(possible / 2)
  const hasEnoughPoints = userPossible >= lockThreshold
  const canLockIn       = isComplete && hasEnoughPoints

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Pool-level lock notice */}
      {poolLocked && (
        <div className="alert alert-info">
          🔒 Bracket locked — the playoffs have started. Picks are final.
        </div>
      )}

      {/* Champion banner */}
      <ChampionBanner champion={champion} sport={sport} teamColors={displayBracket?.teamColors} />

      {/* Score bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-dim)', marginBottom: 10, fontFamily: 'Arial,sans-serif' }}>
        <span>Picks: <strong style={{ color: isComplete ? 'var(--green)' : 'var(--text)' }}>{totalP}/{totalM}</strong></span>
        <span>Correct: <strong style={{ color: 'var(--green)' }}>{earned} pts</strong></span>
        <span>Max: <strong style={{ color: 'var(--gold)' }}>{possible} pts</strong></span>
      </div>

      {/* Last-updated / refresh */}
      <LastUpdated ts={lastUpdated} onRefresh={handleRefresh} refreshing={refreshing} />

      {/* Visual bracket tree — all rounds side-by-side, connector lines, drag-to-scroll */}
      <BracketTree
        rounds={rounds}
        picks={picks}
        feedingMap={feedingMap}
        sport={sport}
        submitted={submitted}
        poolLocked={poolLocked}
        startRoundOrder={startRoundOrder}
        onPick={handlePick}
        teamColors={displayBracket?.teamColors}
      />

      {/* Submit — always visible while picks exist and bracket not locked */}
      {!submitted && totalM > 0 && (
        <div style={{ marginTop: 20 }}>
          <button className="btn btn-gold" onClick={handleSubmit} disabled={!canLockIn}
            style={{ opacity: canLockIn ? 1 : 0.5, cursor: canLockIn ? 'pointer' : 'not-allowed' }}>
            {canLockIn
              ? '🔒 Lock In Bracket'
              : !isComplete
                ? `${totalM - totalP} more pick${totalM - totalP !== 1 ? 's' : ''} needed`
                : `Need ${lockThreshold} possible pts (have ${userPossible})`}
          </button>
          {!canLockIn && !isComplete && (
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              Pick every series across the bracket to lock in
            </div>
          )}
          {!canLockIn && isComplete && !hasEnoughPoints && (
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              {userPossible} / {lockThreshold} possible points · pick more upcoming series
            </div>
          )}
        </div>
      )}

      {submitted && (
        <div className="alert alert-success" style={{ marginTop: 16 }}>
          ✅ Bracket Locked ✓ Points awarded as rounds complete.
        </div>
      )}

      {/* Pattern #2 sticky unlock bar — mirrors the four prior modes
          (GolfDivisionsMode 66f2a6b, GroupChat 52dd228, PickemMode
          4b3e78b, GolfPicksMode da599af) exactly. Fixed at
          bottom:80/z:90, gold border, two-line label + Unlock button.
          The in-flow "Lock In Bracket" button (L714, marginTop:20)
          stays visible — its onClick fires the gated handleSubmit
          which routes to onUnlockRequest, so the user can hit either
          CTA to reach the unlock flow. */}
      {locked && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            left: 16,
            right: 16,
            maxWidth: 500,
            margin: '0 auto',
            background: 'var(--surface2)',
            border: '1px solid var(--gold)',
            borderRadius: 12,
            padding: '12px 14px',
            zIndex: 90,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
              🔒 Pool is locked
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-dim)', marginTop: 2,
              fontFamily: 'Arial, sans-serif',
            }}>
              Unlock to make picks
            </div>
          </div>
          <button
            type="button"
            onClick={onUnlockRequest}
            disabled={unlocking}
            className="btn btn-gold"
            style={{
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 700,
              opacity: unlocking ? 0.6 : 1,
              cursor: unlocking ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {unlocking
              ? 'Unlocking…'
              : (unlockPriceCents != null
                  ? `Unlock $${(unlockPriceCents / 100).toFixed(2)}`
                  : 'Unlock')}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Last-updated chip ─────────────────────────────────────────────────────────

function LastUpdated({ ts, onRefresh, refreshing }) {
  const [, force] = useState(0)
  useEffect(() => {
    if (!ts) return
    const id = setInterval(() => force(n => n + 1), 60_000)
    return () => clearInterval(id)
  }, [ts])
  if (!ts) return null
  const label = timeAgo(ts)
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Arial,sans-serif' }}>
        Updated {label} · auto-refreshes every 5 min
      </span>
      <button onClick={onRefresh} disabled={refreshing}
        style={{ fontSize: 11, color: refreshing ? 'var(--text-muted)' : 'var(--gold)',
          background: 'none', border: '1px solid var(--border)', borderRadius: 6,
          padding: '3px 8px', cursor: refreshing ? 'default' : 'pointer', fontFamily: 'Arial,sans-serif' }}>
        {refreshing ? '…' : '↺ Refresh'}
      </button>
    </div>
  )
}

// ── Champion Banner ───────────────────────────────────────────────────────────

function ChampionBanner({ champion, sport, teamColors }) {
  const meta = champion ? teamColors?.[champion] : null
  return (
    <div style={{ background: champion
        ? 'linear-gradient(135deg,rgba(0,230,118,0.15),rgba(0,230,118,0.05))'
        : 'var(--surface2)',
      border: `1px solid ${champion ? 'rgba(0,230,118,0.4)' : 'var(--border)'}`,
      borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 16,
      display: 'flex', alignItems: 'center', gap: 12 }}>
      {champion && <TeamLogoImg team={champion} sport={sport} size={36}
        color={meta?.color} alternateColor={meta?.alternateColor} abbr={meta?.abbreviation} />}
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'Arial,sans-serif', marginBottom: 2 }}>🏆 YOUR CHAMPION</div>
        <div style={{ fontSize: champion ? 16 : 13, fontWeight: 'bold',
          color: champion ? 'var(--gold)' : 'var(--text-muted)', fontStyle: champion ? 'normal' : 'italic' }}>
          {champion || 'Pick your champion to complete bracket'}
        </div>
      </div>
    </div>
  )
}

// ── Bracket Tree ──────────────────────────────────────────────────────────────
// Renders all rounds as columns left-to-right with SVG connector lines between
// them. Each round-N card occupies 2^N vertical slots so cards in round N+1
// vertically center on the midpoint of their two predecessors — the classic
// March-Madness layout. Drag-to-scroll works on desktop; mobile falls through
// to native touch scrolling via `overflow-x: auto`.

const GOLD     = '#ffd700'
const CARD_W   = 140
const CARD_H   = 70
const COL_GAP  = 44
const SLOT_H   = 90   // per-R1-card vertical slot (cardH + gap)
const LABEL_H  = 34

function BracketTree({ rounds, picks, feedingMap, sport, submitted, poolLocked, startRoundOrder, onPick, teamColors }) {
  const scrollRef = useRef(null)
  // Drag-to-scroll state held in a ref so handlers don't re-bind on every
  // render. hasMoved flips after a small threshold so a tap (no move) still
  // registers a pick on the underlying button.
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0, hasMoved: false })

  function onPointerDown(e) {
    const el = scrollRef.current
    if (!el) return
    // Only fire drag-scroll on left-button or touch — not right-clicks.
    if (e.button !== undefined && e.button !== 0) return
    drag.current.active     = true
    drag.current.startX     = e.pageX - el.offsetLeft
    drag.current.scrollLeft = el.scrollLeft
    drag.current.hasMoved   = false
    el.style.cursor = 'grabbing'
  }
  function onPointerMove(e) {
    const el = scrollRef.current
    if (!el || !drag.current.active) return
    const x  = e.pageX - el.offsetLeft
    const dx = x - drag.current.startX
    if (Math.abs(dx) > 3) drag.current.hasMoved = true
    if (drag.current.hasMoved) {
      el.scrollLeft = drag.current.scrollLeft - dx
      e.preventDefault()
    }
  }
  function onPointerUp() {
    const el = scrollRef.current
    if (el) el.style.cursor = 'grab'
    drag.current.active = false
    // hasMoved stays true until the upcoming synthetic click is suppressed
    // below. Reset on next tick so the very next pointerdown starts fresh.
    if (drag.current.hasMoved) setTimeout(() => { drag.current.hasMoved = false }, 0)
  }
  function onClickCapture(e) {
    if (drag.current.hasMoved) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  if (!rounds.length) return null

  const r1Count = rounds[0].matchups.length
  const totalH  = r1Count * SLOT_H
  const totalW  = rounds.length * CARD_W + (rounds.length - 1) * COL_GAP

  const cardTop    = (r, i) => i * Math.pow(2, r) * SLOT_H + (Math.pow(2, r) * SLOT_H - CARD_H) / 2
  const cardCenter = (r, i) => cardTop(r, i) + CARD_H / 2
  const colLeft    = (r)    => r * (CARD_W + COL_GAP)

  return (
    <div
      ref={scrollRef}
      onMouseDown={onPointerDown}
      onMouseMove={onPointerMove}
      onMouseUp={onPointerUp}
      onMouseLeave={onPointerUp}
      onClickCapture={onClickCapture}
      style={{
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        cursor: 'grab',
        userSelect: 'none',
        marginBottom: 16,
        paddingBottom: 8,
        scrollbarWidth: 'thin',
      }}
    >
      <div style={{ position: 'relative', width: totalW }}>
        {/* Column labels */}
        <div style={{ display: 'flex', gap: COL_GAP, marginBottom: 8, height: LABEL_H }}>
          {rounds.map((round, r) => (
            <div key={r} style={{ width: CARD_W, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#888', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700 }}>
                {round.name}
              </div>
              <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
                {round.points} pt{round.points !== 1 ? 's' : ''} each
              </div>
            </div>
          ))}
        </div>

        {/* Bracket area (cards + connectors) */}
        <div style={{ position: 'relative', width: totalW, height: totalH }}>
          {/* Connector lines between adjacent columns */}
          {rounds.slice(0, -1).map((round, r) => (
            <svg
              key={`conn-${r}`}
              style={{ position: 'absolute', left: colLeft(r) + CARD_W, top: 0, width: COL_GAP, height: totalH, pointerEvents: 'none' }}
            >
              {round.matchups.map((_, i) => {
                const targetI = Math.floor(i / 2)
                const sourceY = cardCenter(r, i)
                const targetY = cardCenter(r + 1, targetI)
                const mid     = COL_GAP / 2
                // Source card winner advances to target → highlight the line
                // gold when ESPN confirmed OR user picked that team to win.
                const m       = round.matchups[i]
                const winnerName = m.winner || getPickForMatchup(picks, m)
                const isAdvancing = !!winnerName && (winnerName === m.team1 || winnerName === m.team2)
                return (
                  <path
                    key={i}
                    d={`M 0 ${sourceY} H ${mid} V ${targetY} H ${COL_GAP}`}
                    fill="none"
                    stroke={isAdvancing ? GOLD : '#2a2a2a'}
                    strokeWidth={isAdvancing ? 1.5 : 1}
                    opacity={isAdvancing ? 0.9 : 1}
                  />
                )
              })}
            </svg>
          ))}

          {/* Matchup cards */}
          {rounds.flatMap((round, r) =>
            round.matchups.map((m, i) => {
              const isPlaceholder = m.team1 === 'TBD' && m.team2 === 'TBD'
              const isPastStart   = (m.roundOrder || 1) >= startRoundOrder
              const locked        = poolLocked || submitted || !!m.winner || !isPastStart || isPlaceholder
              const feedingPick1  = getUserPickForFeedingSlot(r, i, 0, rounds, feedingMap, picks)
              const feedingPick2  = getUserPickForFeedingSlot(r, i, 1, rounds, feedingMap, picks)
              // Per-round point value, mirroring the fallback pattern used at
              // lines 634/644 for possible-points math. Drives the inline
              // "+Npts" label rendered next to each graded series's picked row.
              const roundPts      = round.points || [1, 2, 4, 8][r] || 0
              return (
                <MatchupCard
                  key={`${r}-${i}`}
                  matchup={m}
                  pick={getPickForMatchup(picks, m)}
                  sport={sport}
                  locked={locked}
                  onPick={(team) => onPick(m, team)}
                  feedingPick1={feedingPick1}
                  feedingPick2={feedingPick2}
                  roundPoints={roundPts}
                  teamColors={teamColors}
                  positionStyle={{
                    position: 'absolute',
                    left: colLeft(r),
                    top:  cardTop(r, i),
                    width: CARD_W,
                    height: CARD_H,
                  }}
                />
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

// ── Matchup Card ──────────────────────────────────────────────────────────────
// Compact ~140×70 card with two team rows. Placeholder TBD slot rendered as a
// dashed-border ghost. Picked team gold, winner green, loser dimmed.

function MatchupCard({ matchup, pick, sport, locked, onPick, positionStyle, feedingPick1, feedingPick2, roundPoints, teamColors }) {
  const { team1, team2, seed1, seed2, wins1, wins2, winner } = matchup
  const isPlaceholder = team1 === 'TBD' && team2 === 'TBD'
  const inProgress    = !winner && ((wins1 || 0) > 0 || (wins2 || 0) > 0)
  const isFinal       = !!winner

  if (isPlaceholder) {
    return (
      <div style={{
        ...positionStyle,
        background: '#0d0d0d',
        border: '1.5px dashed #2a2a2a',
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, color: '#555', fontStyle: 'italic', letterSpacing: 0.4,
      }}>
        TBD
      </div>
    )
  }

  // Border styling per state so completed series still register at a glance.
  // Previously completed and unstarted cards shared #2a2a2a which made R1
  // (often all-completed by deep playoffs) blend into the page background.
  const borderColor = inProgress ? 'rgba(0,230,118,0.4)'
                    : isFinal    ? 'rgba(255,255,255,0.12)'
                    :              '#2a2a2a'

  return (
    <div style={{
      ...positionStyle,
      background: '#1a1a1a',
      border: `1px solid ${borderColor}`,
      borderRadius: 8,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <TeamRow
        team={team1} seed={seed1} wins={wins1}
        sport={sport}
        teamColors={teamColors}
        isPicked={pick === team1}
        isWinner={winner === team1}
        isLoser={!!winner && winner !== team1}
        locked={locked}
        onClick={() => onPick(team1)}
        userPickBelow={feedingPick1}
        roundPoints={roundPoints}
      />
      <div style={{ height: 1, background: '#2a2a2a' }} />
      <TeamRow
        team={team2} seed={seed2} wins={wins2}
        sport={sport}
        teamColors={teamColors}
        isPicked={pick === team2}
        isWinner={winner === team2}
        isLoser={!!winner && winner !== team2}
        locked={locked}
        onClick={() => onPick(team2)}
        userPickBelow={feedingPick2}
        roundPoints={roundPoints}
      />
    </div>
  )
}

// ── Team Row ──────────────────────────────────────────────────────────────────

function TeamRow({ team, seed, wins, sport, isPicked, isWinner, isLoser, locked, onClick, userPickBelow, roundPoints, teamColors }) {
  const meta = team ? teamColors?.[team] : null
  const isTBD = !team || team === 'TBD'

  // Five visual states. Order matters — earlier branches dominate.
  //   isPicked + isWinner   → correct pick:  gold name, green ✓ badge
  //   isPicked + isLoser    → wrong pick:    gold name, red strikethrough
  //   isPicked alone        → pending pick:  gold name, gold tint
  //   isWinner (not picked) → ESPN winner:   plain white, no celebration
  //   isLoser  (not picked) → loser:         gray, red strikethrough
  //   default               → neutral row, white text
  //
  // The user-affordance signal (gold = "your pick") is preserved across
  // pending/correct/wrong. Correctness is layered on top via the ✓ badge,
  // not by overwriting the gold treatment. ESPN winners the user didn't
  // pick get no celebration — they're informational, not a user win.
  const correct = isPicked && isWinner
  const wrong   = isPicked && isLoser
  const pending = isPicked && !isWinner && !isLoser

  const bg =
    correct ? 'rgba(0,230,118,0.10)' :
    pending ? 'rgba(255,215,0,0.10)' :
              'transparent'

  const color =
    isTBD                        ? '#555' :
    isPicked                     ? GOLD  :  // covers correct + wrong + pending
    isWinner                     ? '#fff' :  // ESPN winner you didn't pick: plain white
    isLoser                      ? '#888' :  // plain loser: gray
                                   '#fff'

  const struckThrough = isLoser  // both wrong-pick and unpicked-loser get red strike
  const winsCountColor =
    correct  ? '#00e676' :
    isPicked ? GOLD      :
    isWinner ? '#fff'    :
               '#888'

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={locked || isTBD}
      style={{
        flex: 1,
        background: bg,
        border: 'none',
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '0 8px',
        cursor: locked || isTBD ? 'default' : 'pointer',
        textAlign: 'left',
        minWidth: 0,
        color,
        opacity: struckThrough ? 0.75 : 1,
        fontFamily: 'inherit',
      }}
    >
      {/* Correct-pick badge: green ✓ before the logo. Scannable signal of "you got this right." */}
      {correct && (
        <span style={{
          width: 14, height: 14, borderRadius: '50%',
          background: '#00e676',
          color: '#000',
          fontSize: 10, fontWeight: 900,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>✓</span>
      )}
      {/* Wrong-pick badge: red ✗ before the logo. Visual parallel to the correct-pick ✓. */}
      {wrong && (
        <span style={{
          width: 14, height: 14, borderRadius: '50%',
          background: '#ff5252',
          color: '#fff',
          fontSize: 10, fontWeight: 900,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>✗</span>
      )}
      {!isTBD && <TeamLogoImg team={team} sport={sport} size={20}
        color={meta?.color} alternateColor={meta?.alternateColor} abbr={meta?.abbreviation} />}
      {seed != null && (
        <span style={{ fontSize: 9, color: '#666', flexShrink: 0, width: 14 }}>#{seed}</span>
      )}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <span style={{
          fontSize: 12,
          fontWeight: isPicked || isWinner ? 700 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          textDecoration: struckThrough ? 'line-through' : 'none',
          textDecorationColor: struckThrough ? '#ff5252' : 'inherit',
          textDecorationThickness: struckThrough ? '2px' : 'auto',
        }}>
          {isTBD ? 'TBD' : team.split(' ').pop()}
        </span>
        {/* Phantom pick: the user's wrong feeding-round pick that would
            have been sitting in this slot. Only set on missed slots —
            getUserPickForFeedingSlot returns null for correct picks. */}
        {userPickBelow && (
          <span style={{
            fontSize: 10,
            color: GOLD,
            opacity: 0.7,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textDecoration: 'line-through',
            textDecorationColor: '#ff5252',
            textDecorationThickness: '1.5px',
            marginTop: 1,
          }}>
            {userPickBelow.split(' ').pop()}
          </span>
        )}
      </div>
      {wins != null && (wins > 0 || isWinner || isLoser) && (
        <span style={{ fontSize: 11, color: winsCountColor, flexShrink: 0, fontWeight: 600 }}>
          {wins}
        </span>
      )}
      {/* Points earned on graded CORRECT picks only. Wrong picks already read
          as such via the red ✗ badge + strikethrough — the "+0pts" was
          redundant and cramping the compact matchup cards (team names
          truncating to "A..."). Pending/TBD/unpicked rows: no label. */}
      {correct && (
        <span style={{
          fontSize: 11,
          fontWeight: 'bold',
          fontFamily: 'Arial, sans-serif',
          color: '#00e676',
          flexShrink: 0,
          marginLeft: 4,
        }}>
          +{roundPoints}pts
        </span>
      )}
    </button>
  )
}

// ── Bracket Results Feed ──────────────────────────────────────────────────────
// Renders the Results tab content for bracket groups as a per-round sub-tab
// layout: tap R1 to see just R1's matchups, tap Finals to see just those.
// Three matchup states (derived from the bracket payload — no separate
// per-series result column):
//   GRADED      — matchup.winner !== null
//   IN PROGRESS — !winner && (wins1 > 0 || wins2 > 0)
//   UPCOMING    — !winner && wins1 === 0 && wins2 === 0
//
// TBD-vs-TBD matchups are skipped (future-round slots waiting for teams to
// advance — the bracket tree shows them structurally; the feed doesn't
// duplicate). Half-TBD matchups (one team known, one TBD) are KEPT — the user
// may have a pick on the known team they want to track.
//
// Tab strip mirrors the Leaderboard sub-tab pattern (App.jsx:3568-3570 —
// `.tab-bar` + `.tab-item ${active?'active':''}`) for visual continuity.
// ALL rounds the sport has are rendered as tabs (4 entries from
// PLAYOFF_ROUND_INFO, surfaced via bracket.rounds), even if a round is
// all-TBD — the strip doesn't pop tabs in/out as the tournament progresses.
//
// Default-active-round logic: latest round with any graded series →
// latest round with any in-progress series → first round. Computed inline
// when activeRoundIdx state is null (first render after bracket load); user
// clicks override.
//
// Within the active round: matchups bucket-sorted graded → in-progress →
// upcoming so freshest outcomes lead. Per-round summary line ("YOUR RESULTS
// · X of Y · Npt") only renders when the round has graded picks — pre-event
// rounds get no "0 of 0" noise.
//
// Scope: current user only. Cross-member drill-down deferred to F2.

// Short labels for the tab strip. Per-sport mapping covers the 4-round
// canonical PLAYOFF_ROUND_INFO entries (odds.js:172-197). Fallback `R${i+1}`
// covers any unexpected name.
const SHORT_ROUND_LABEL = {
  'First Round':              'R1',
  'Semifinals':               'R2',
  'Second Round':             'R2',
  'Conference Finals':        'CF',
  'Conference Championship':  'CC',
  'Finals':                   'Finals',
  'Stanley Cup Final':        'SCF',
  'Wild Card':                'WC',
  'Division Series':          'DS',
  'Divisional':               'DIV',
  'Championship Series':      'CS',
  'World Series':             'WS',
  'Super Bowl':               'SB',
}
function shortRoundLabel(name, idx) {
  return SHORT_ROUND_LABEL[name] || `R${idx + 1}`
}

export function BracketResultsFeed({ group, user }) {
  const [bracket, setBracket]               = useState(null)
  const [picks, setPicks]                   = useState(new Map())
  const [loading, setLoading]               = useState(true)
  // null = use computed default; non-null = explicit user selection.
  const [activeRoundIdx, setActiveRoundIdx] = useState(null)

  const sport = normalizeSport(group.sport)

  useEffect(() => {
    if (!user?.id || !group?.id) { setLoading(false); return }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const [saved, live] = await Promise.all([
        loadBracket(group.id, user.id),
        fetchPlayoffBracket(sport),
      ])
      if (cancelled) return
      setBracket(live)
      const v2 = migratePicksV1toV2(saved?.picks, live)
      setPicks(picksArrayToMap(v2))
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [group?.id, user?.id, sport])

  if (loading) return (
    <div style={{ textAlign:'center', padding:'40px', color:'var(--text-dim)' }}>
      <div style={{ width:'24px', height:'24px', border:'2px solid var(--border)', borderTopColor:'var(--gold)', borderRadius:'50%', margin:'0 auto 12px', animation:'spin .8s linear infinite' }} />
      Loading results…
    </div>
  )

  // Build all rounds (keep all-TBD rounds — the tab strip stays stable
  // even before matchups exist for a round). Within each round, bucket-
  // sort matchups graded → in-progress → upcoming so the round's freshest
  // outcomes appear at the top of its tab.
  const rounds = (bracket?.rounds || []).map((round, idx) => {
    const real = round.matchups.filter(m => !(m.team1 === 'TBD' && m.team2 === 'TBD'))
    const graded = [], inProg = [], upcoming = []
    for (const m of real) {
      if (m.winner) graded.push(m)
      else if ((m.wins1 || 0) > 0 || (m.wins2 || 0) > 0) inProg.push(m)
      else upcoming.push(m)
    }
    const roundOrder = round.matchups[0]?.roundOrder || (idx + 1)
    const pts        = round.points || [1, 2, 4, 8][idx] || 0
    return {
      name:       round.name,
      points:     pts,
      roundOrder,
      matchups:   [...graded, ...inProg, ...upcoming],
      hasGraded:  graded.length > 0,
      hasInProg:  inProg.length > 0,
    }
  })

  // Whole-bracket fallback: no real matchups anywhere — pre-tournament edge
  // case. Don't render the tab strip in this state.
  if (rounds.length === 0 || rounds.every(r => r.matchups.length === 0)) return (
    <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--text-dim)' }}>
      <div style={{ fontSize:'40px', marginBottom:'10px' }}>📭</div>
      <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--text)' }}>No series yet</div>
      <div style={{ fontSize:'12px', marginTop:'6px' }}>Matchups will appear here once the bracket is set</div>
    </div>
  )

  // Default active round: latest graded > latest in-progress > first.
  // Click sets activeRoundIdx explicitly; here we fall back to the
  // computed default until the user has clicked.
  let effectiveActiveIdx = activeRoundIdx
  if (effectiveActiveIdx === null) {
    effectiveActiveIdx = 0
    for (let i = rounds.length - 1; i >= 0; i--) {
      if (rounds[i].hasGraded) { effectiveActiveIdx = i; break }
    }
    if (effectiveActiveIdx === 0 && !rounds[0].hasGraded) {
      for (let i = rounds.length - 1; i >= 0; i--) {
        if (rounds[i].hasInProg) { effectiveActiveIdx = i; break }
      }
    }
  }
  // Guard against an explicit selection that became out-of-bounds (theoretical;
  // bracket.rounds.length is stable at 4 for supported sports).
  if (effectiveActiveIdx >= rounds.length) effectiveActiveIdx = 0

  const activeRound = rounds[effectiveActiveIdx]

  // Per-round summary stats — scoped to the active round only.
  let activeCorrect = 0, activeGraded = 0, activePts = 0
  for (const m of activeRound.matchups) {
    if (!m.winner) continue
    activeGraded++
    const pick = picks.get(`${m.roundAbbr}:${m.slot}`)
    if (pick && pick === m.winner) {
      activeCorrect++
      activePts += activeRound.points
    }
  }

  return (
    <div>
      <div className="tab-bar" style={{ marginBottom:'10px' }}>
        {rounds.map((round, idx) => (
          <button
            key={round.roundOrder}
            className={`tab-item ${effectiveActiveIdx === idx ? 'active' : ''}`}
            onClick={() => setActiveRoundIdx(idx)}
          >
            {shortRoundLabel(round.name, idx)}
          </button>
        ))}
      </div>

      {/* Round-context caption — full name + points value for the active round */}
      <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginBottom:'12px', textAlign:'center', letterSpacing:'0.5px' }}>
        {activeRound.name} · {activeRound.points} pt{activeRound.points !== 1 ? 's' : ''} each
      </div>

      {/* Per-round summary — suppressed when this round has no graded picks
          (pre-event or pure-upcoming) so we don't render "0 of 0 · 0pt". */}
      {activeGraded > 0 && (
        <div className="card" style={{ marginBottom:'12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:'12px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', letterSpacing:'0.5px' }}>YOUR RESULTS</span>
          <span style={{ fontSize:'13px', fontFamily:'Arial,sans-serif' }}>
            <span style={{ color:'var(--text)' }}>{activeCorrect} of {activeGraded}</span>
            <span style={{ color:'var(--gold)', fontWeight:'bold', marginLeft:'12px' }}>{activePts}pt</span>
          </span>
        </div>
      )}

      {/* Active round content — per-round empty state when this round is
          all-TBD; otherwise the bucketed matchup cards. */}
      {activeRound.matchups.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--text-dim)' }}>
          <div style={{ fontSize:'40px', marginBottom:'10px' }}>⏳</div>
          <div style={{ fontSize:'14px', fontWeight:'bold', color:'var(--text)' }}>
            {activeRound.name} hasn't started
          </div>
          <div style={{ fontSize:'12px', marginTop:'6px' }}>
            Matchups appear once the previous round finishes
          </div>
        </div>
      ) : (
        activeRound.matchups.map(m => renderBracketSeriesCard(m, picks, activeRound.points, sport, bracket?.teamColors))
      )}
    </div>
  )
}

// Renders a single series card for the feed. Layout:
//   ┌─ state badge ─────────────── start-date (upcoming only) ─┐
//   │ #seed  [logo] Team Name                            wins  │
//   │ #seed  [logo] Team Name                            wins  │
//   │ ──────────────────────────────────────────────────────── │
//   │ Your pick: <team> <✓/✗>                          +Npts   │
//   └──────────────────────────────────────────────────────────┘
// The team-line color/highlight encodes objective series facts (winner gold,
// loser strikethrough). The footer encodes the user-specific state (correct/
// wrong/pending/upcoming) using the same visual vocabulary as the bracket
// tree — ✓/✗ badges, GOLD for the picked team, #00e676 for "+Npts".
function renderBracketSeriesCard(m, picks, roundPoints, sport, teamColors) {
  const key      = pickKey(m)
  const userPick = key ? picks.get(key) : null

  const isFinal      = !!m.winner
  const isInProgress = !isFinal && ((m.wins1 || 0) > 0 || (m.wins2 || 0) > 0)
  const isUpcoming   = !isFinal && !isInProgress

  const correct = isFinal && !!userPick && userPick === m.winner
  const wrong   = isFinal && !!userPick && userPick !== m.winner

  // Header badge — left side is the state, right side is the start date
  // only when upcoming (gives a "Starts Mon Jun 2" affordance).
  const stateLabel =
    isFinal      ? { text: 'FINAL',       color: 'var(--text-muted)' } :
    isInProgress ? { text: 'IN PROGRESS', color: '#00e676' } :
                   { text: 'UPCOMING',    color: 'var(--text-muted)' }

  let startDateStr = null
  if (isUpcoming && m.firstGameDate) {
    try {
      const d = new Date(m.firstGameDate + 'T12:00:00')
      startDateStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    } catch (_) {}
  }

  // Team-line renderer — pure series-facts. Winner gold, loser gray-with-
  // strikethrough on finals; both white otherwise.
  function teamLine(team, seed, wins) {
    const isTBD       = !team || team === 'TBD'
    const isWinnerRow = isFinal && team === m.winner
    const isLoserRow  = isFinal && !!m.winner && team !== m.winner && !isTBD
    const color       = isTBD       ? '#555' :
                        isWinnerRow ? GOLD :
                        isLoserRow  ? '#888' :
                                      '#fff'
    const winsColor   = isTBD       ? '#555' :
                        isWinnerRow ? GOLD :
                        isLoserRow  ? '#888' :
                                      '#aaa'
    return (
      <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'4px 0' }}>
        <span style={{ fontSize:'10px', color:'#666', width:'18px', flexShrink:0, fontFamily:'Arial, sans-serif' }}>
          {seed != null ? `#${seed}` : ''}
        </span>
        {!isTBD && (() => {
          const meta = teamColors?.[team]
          return <TeamLogoImg team={team} sport={sport} size={20}
            color={meta?.color} alternateColor={meta?.alternateColor} abbr={meta?.abbreviation} />
        })()}
        {isTBD && <div style={{ width:'20px', height:'20px', flexShrink:0 }} />}
        <span style={{
          flex:1, minWidth:0, fontSize:'13px', fontWeight: isWinnerRow ? 700 : 500,
          color, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          textDecoration: isLoserRow ? 'line-through' : 'none',
          textDecorationColor: isLoserRow ? '#ff5252' : 'inherit',
          textDecorationThickness: isLoserRow ? '2px' : 'auto',
        }}>
          {isTBD ? 'TBD' : team}
        </span>
        <span style={{ fontSize:'13px', color:winsColor, fontWeight:600, fontFamily:'Arial, sans-serif', minWidth:'18px', textAlign:'right' }}>
          {wins != null ? wins : ''}
        </span>
      </div>
    )
  }

  return (
    <div key={`${m.roundAbbr}:${m.slot}`} className="card" style={{ marginBottom:'10px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
        <span style={{ fontSize:'10px', color:stateLabel.color, fontFamily:'Arial, sans-serif', letterSpacing:'0.8px', fontWeight:700 }}>
          {stateLabel.text}
        </span>
        {startDateStr && (
          <span style={{ fontSize:'10px', color:'var(--text-dim)', fontFamily:'Arial, sans-serif' }}>
            Starts {startDateStr}
          </span>
        )}
      </div>

      {teamLine(m.team1, m.seed1, m.wins1)}
      {teamLine(m.team2, m.seed2, m.wins2)}

      {/* User pick footer */}
      <div style={{ borderTop:'1px solid var(--border)', marginTop:'8px', paddingTop:'8px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px' }}>
        {!userPick ? (
          <span style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'Arial, sans-serif' }}>
            No pick
          </span>
        ) : (
          <>
            <span style={{ fontSize:'12px', fontFamily:'Arial, sans-serif', display:'inline-flex', alignItems:'center', gap:'6px' }}>
              <span style={{ color:'var(--text-dim)' }}>Your pick:</span>
              <span style={{
                color: correct ? '#00e676' : wrong ? '#ff5252' : GOLD,
                fontWeight: 700,
                textDecoration: wrong ? 'line-through' : 'none',
                textDecorationColor: wrong ? '#ff5252' : 'inherit',
              }}>
                {userPick}
              </span>
              {correct && (
                <span style={{
                  width: 14, height: 14, borderRadius: '50%',
                  background: '#00e676', color: '#000',
                  fontSize: 10, fontWeight: 900,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>✓</span>
              )}
              {wrong && (
                <span style={{
                  width: 14, height: 14, borderRadius: '50%',
                  background: '#ff5252', color: '#fff',
                  fontSize: 10, fontWeight: 900,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>✗</span>
              )}
              {isInProgress && (
                <span style={{ color:'var(--text-dim)', fontWeight:400 }}>· pending</span>
              )}
            </span>
            {correct && (
              <span style={{
                fontSize: 11, fontWeight: 'bold', fontFamily: 'Arial, sans-serif',
                color: '#00e676', whiteSpace: 'nowrap',
              }}>
                +{roundPoints}pts
              </span>
            )}
          </>
        )}
      </div>
    </div>
  )
}
